import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Autocomplete, Chip, TextField, Typography, lighten } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

export interface OptionType {
  label: string;
  value: string;
  count?: number;
  exclude?: boolean;
  titleType?: string;
}

interface Props {
  options: OptionType[];
  variant?: 'background' | 'white';
  selected?: OptionType | OptionType[] | null;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  multiple?: boolean;
  limitTags?: number;
  disableClearable?: boolean;
  style?: React.CSSProperties;
  error?: boolean;
  errorText?: string;
  /** @deprecated Legacy prop – alias for selected */
  value?: string;
  /** @deprecated Legacy prop – kept for backward compat */
  disableBorder?: boolean;
  /** @deprecated Legacy prop – kept for backward compat */
  loading?: boolean;
  /** @deprecated Legacy prop – kept for backward compat */
  listItemActionText?: string;
  /** @deprecated Legacy prop – kept for backward compat */
  onMenuItemClick?: (id: string) => () => void;
  /** @deprecated Legacy prop – kept for backward compat */
  onKeyUp?(): void;
  /** @deprecated Legacy prop – kept for backward compat */
  onKeyDown?(): void;

  onChange: (...args: any[]) => void;
}

const PaiAutoComplete: React.FC<Props> = ({
  options,
  variant = 'white',
  selected,
  placeholder,
  label,
  disabled,
  multiple,
  limitTags,
  disableClearable,
  style,
  error,
  errorText,
  onChange,
}) => {
  const { classes, cx } = useStyles({ variant, error });

  return (
    <div style={{ width: style?.width || '100%', maxWidth: style?.maxWidth || undefined }}>
      {!_.isNil(label) ? <Typography className={classes.label}>{label}</Typography> : null}
      <Autocomplete
        className={classes.autoComplete}
        classes={{ input: classes.autoCompleteInput, paper: classes.paper }}
        ListboxProps={{ className: classes.listBox }}
        options={options}
        value={selected!}
        isOptionEqualToValue={(option, value) => option?.value === value?.value || value.value === ''}
        getOptionLabel={(option) => option?.label}
        onChange={onChange}
        disabled={disabled}
        disableClearable={disableClearable}
        disableCloseOnSelect={multiple}
        multiple={multiple}
        limitTags={!_.isNil(limitTags) ? limitTags : 1}
        renderTags={(value: OptionType[], getTagProps) =>
          value.map((option: OptionType, index: number) => (
            // eslint-disable-next-line react/jsx-key
            (<Chip
              variant="outlined"
              size={'small'}
              label={option?.label}
              {...getTagProps({ index })}
              style={{ borderRadius: '4px', background: '#FFF' }}
            />)
          ))
        }
        popupIcon={<ExpandMoreIcon />}
        renderOption={(props, option) => (
          <li
            {...props}
            key={option?.value}
            className={cx(
              classes.listItem,
              (
                !multiple
                  ? option?.value === (selected as OptionType)?.value
                  : (selected as OptionType[])?.some((item) => item.value === option.value)
              )
                ? 'selected'
                : null
            )}
            style={{ display: option?.value !== '' ? undefined : 'none' }}
          >
            {option?.label}
          </li>
        )}
        renderInput={(params: any) => <TextField {...params} placeholder={placeholder} variant={'outlined'} fullWidth={true} />}
        openOnFocus={false}
        style={style}
      />
      {!!error && !_.isNil(errorText) ? <Typography className={classes.errorText}>{errorText}</Typography> : null}
    </div>
  );
};

const useStyles = makeStyles<{ variant?: 'background' | 'white'; error?: boolean }>()((theme, { variant, error }) => ({
  autoComplete: {
    '& .MuiOutlinedInput-root': {
      background: error ? lighten(theme.palette.error.main, 0.9) : variant === 'background' ? theme.palette.backgrounds.main_new : '#FFF',
      borderRadius: '10px',
      padding: '2.5px',
    },
    '& .MuiSvgIcon-root': {
      color: error ? theme.palette.error.main : theme.palette.primary.main,
    },
    '&:hover, focus, .Mui-focused': {
      '& .MuiOutlinedInput-notchedOutline': {
        border: variant === 'background' ? 'none' : error ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.primary.main}`,
        borderRadius: '10px',
      },
    },
    '& .MuiOutlinedInput-notchedOutline': {
      border: variant === 'background' ? 'none' : error ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.primary.main}`,
      borderRadius: '10px',
    },
    '& .Mui-disabled': {
      '& .MuiOutlinedInput-notchedOutline': {
        border: `1px solid ${theme.palette.action.disabled}`,
        borderRadius: '10px',
      },
      '& .MuiSvgIcon-root': {
        color: theme.palette.action.disabled,
      },
    },
  },
  autoCompleteInput: {
    padding: '6.5px 12px !important',
    fontSize: theme.typography.pxToRem(15),
    background: error ? lighten(theme.palette.error.main, 0.9) : variant === 'background' ? theme.palette.backgrounds.main_new : '#FFF',
    minHeight: 'calc(39px - 8px - 8px)',
    '&::placeholder': {
      opacity: 0.5,
      fontSize: theme.typography.pxToRem(15),
    },
  },
  listBox: {
    padding: 0,
  },
  paper: {
    borderRadius: '6px',
  },
  listItem: {
    cursor: 'pointer',
    fontSize: theme.typography.pxToRem(13),
    borderBottom: `1px solid ${theme.palette.primary.main}`,
    padding: '6px 16px',
    '&:last-of-type': {
      borderBottom: 'none',
    },
    '&.selected': {
      background: theme.palette.backgrounds.main,
      opacity: 1,
    },
    '&:hover': {
      background: theme.palette.backgrounds.main,
      opacity: 1,
    },
  },
  label: {
    marginBottom: '4px',
    marginLeft: '4px',
    fontSize: theme.typography.pxToRem(14),
  },
  errorText: {
    fontSize: theme.typography.pxToRem(10),
    marginTop: '4px',
    textAlign: 'right',
    color: theme.palette.error.main,
    padding: '0 4px',
  },
}));

export default PaiAutoComplete;
